import type { ProductAttributeState } from './useProductAttributeStates';
import { useEffect, useRef, useState } from 'react';
import { useStationeryProductPricingContext } from '../useStationeryProductPricingContext';
import { useTaylorMappings } from '../mappings/taylor.mapping';
import { useJoyDigitalMappings } from '../mappings/joyDigital.mapping';
import type { StationeryProduct } from '@shared/utils/print-price/types';

export type AddAttributeFunction = (newAttribute: ProductAttributeState, product: StationeryProduct) => void;
export type MappingFunction = (addAttribute: AddAttributeFunction, product: StationeryProduct) => void;

const useMapStringToFunction = (mapString: string): MappingFunction => {
  const taylorMapping = useTaylorMappings();
  const joyDigitalMapping = useJoyDigitalMappings();
  const freeDigitalMapping = () => {};

  switch (mapString) {
    case 'taylormap': {
      return taylorMapping;
    }
    case 'joydigitalmap': {
      return joyDigitalMapping;
    }
    case 'freedigitalmap': {
      return freeDigitalMapping;
    }
    default: {
      throw new Error('Improperly configured product <--> attribute map');
    }
  }
};

/**
 * Defines how product attributes are mapped to the draft.
 */
export const useRegisterAttributeLinks = (addAttribute: AddAttributeFunction, product: StationeryProduct | undefined, skip?: boolean) => {
  const { activeProduct } = useStationeryProductPricingContext();

  const registerDraftMapping = useMapStringToFunction(activeProduct.mappingFunction);

  const lastActiveProduct = useRef(activeProduct.id);
  useEffect(() => {
    if (activeProduct.id !== lastActiveProduct.current) {
      lastActiveProduct.current = activeProduct.id;
      setHasSetupAttributes(false);
    }
  }, [activeProduct.id]);

  // Create Attribute <--> Draft connection
  const [hasSetupAttributes, setHasSetupAttributes] = useState(false);
  useEffect(() => {
    if (!hasSetupAttributes && product && !skip) {
      registerDraftMapping(addAttribute, product);
      setHasSetupAttributes(true);
    }
  }, [registerDraftMapping, hasSetupAttributes, addAttribute, product, skip]);
};
